import React, { useState, useEffect, useRef } from 'react';
import './SolutionSearch.css';

export interface SearchSuggestion {
  text: string;
  type: 'name' | 'company' | 'category' | 'keyword' | 'tag' | 'use_case';
  count?: number;
}

export interface SolutionSearchProps {
  searchTerm: string;
  suggestions?: SearchSuggestion[];
  recentSearches?: string[];
  loading?: boolean;
  placeholder?: string;
  showSuggestions?: boolean;
  showRecentSearches?: boolean;
  maxSuggestions?: number;
  maxRecentSearches?: number;
  minSearchLength?: number;
  onSearch: (searchTerm: string) => void;
  onSuggestionClick?: (suggestion: SearchSuggestion) => void;
  onClear?: () => void;
  className?: string;
}

const SolutionSearch: React.FC<SolutionSearchProps> = ({
  searchTerm,
  suggestions = [],
  recentSearches = [],
  loading = false,
  placeholder = 'Search AI solutions...',
  showSuggestions = true,
  showRecentSearches = true,
  maxSuggestions = 8,
  maxRecentSearches = 5,
  minSearchLength = 2,
  onSearch,
  onSuggestionClick,
  onClear,
  className = '',
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [isFocused, setIsFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [localRecentSearches, setLocalRecentSearches] = useState<string[]>(recentSearches);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('ai-solutions-recent-searches');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setLocalRecentSearches(parsed.slice(0, maxRecentSearches));
      } catch (error) {
        console.warn('Failed to parse recent searches from localStorage');
      }
    }
  }, [maxRecentSearches]);

  // Update local search term when prop changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // No auto-search - only manual search on Enter or suggestion click

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    setSelectedIndex(-1);
    
    // Show dropdown if we have enough characters for search OR if we have recent searches
    const shouldShowDropdown = (
      (value.length >= minSearchLength) || 
      (showRecentSearches && localRecentSearches.length > 0)
    );
    setShowDropdown(shouldShowDropdown);
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsFocused(true);
    const shouldShowDropdown = (
      (localSearchTerm.length >= minSearchLength) || 
      (showRecentSearches && localRecentSearches.length > 0)
    );
    setShowDropdown(shouldShowDropdown);
  };

  // Handle input blur
  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => {
      setIsFocused(false);
      setShowDropdown(false);
      setSelectedIndex(-1);
    }, 150);
  };

  // Handle search submission (only on Enter or manual trigger)
  const handleSearch = (term: string) => {
    const trimmedTerm = term.trim();
    // Always perform search, even if empty (for clearing)
    addToRecentSearches(trimmedTerm);
    onSearch(trimmedTerm);
    setLocalSearchTerm(trimmedTerm);
    setShowDropdown(false);
    inputRef.current?.blur();
  };

  // Add to recent searches (only for non-empty terms)
  const addToRecentSearches = (term: string) => {
    if (term.length > 0) {
      const updated = [term, ...localRecentSearches.filter(s => s !== term)].slice(0, maxRecentSearches);
      setLocalRecentSearches(updated);
      localStorage.setItem('ai-solutions-recent-searches', JSON.stringify(updated));
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    const searchText = suggestion.text;
    setLocalSearchTerm(searchText);
    handleSearch(searchText);
    
    if (onSuggestionClick) {
      onSuggestionClick(suggestion);
    }
  };

  // Handle recent search click
  const handleRecentSearchClick = (term: string) => {
    setLocalSearchTerm(term);
    handleSearch(term);
  };

  // Handle clear
  const handleClear = () => {
    setLocalSearchTerm('');
    setShowDropdown(false);
    onSearch(''); // Immediately clear search results
    if (onClear) {
      onClear();
    }
    inputRef.current?.focus();
  };

  // Handle key navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    const totalItems = (showSuggestions ? suggestions.slice(0, maxSuggestions).length : 0) +
                      (showRecentSearches ? localRecentSearches.slice(0, maxRecentSearches).length : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          // User selected a suggestion or recent search
          const suggestionsCount = showSuggestions ? filteredSuggestions.length : 0;
          if (selectedIndex < suggestionsCount) {
            handleSuggestionClick(filteredSuggestions[selectedIndex]);
          } else {
            const recentIndex = selectedIndex - suggestionsCount;
            handleRecentSearchClick(filteredRecentSearches[recentIndex]);
          }
        } else {
          // User pressed Enter with their typed text
          handleSearch(localSearchTerm);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Filter suggestions based on search term
  const filteredSuggestions = showSuggestions 
    ? suggestions.filter(s => 
        localSearchTerm.length < minSearchLength || 
        s.text.toLowerCase().includes(localSearchTerm.toLowerCase())
      ).slice(0, maxSuggestions)
    : [];

  // Filter recent searches
  const filteredRecentSearches = showRecentSearches
    ? localRecentSearches.filter(s =>
        localSearchTerm.length < minSearchLength ||
        s.toLowerCase().includes(localSearchTerm.toLowerCase())
      ).slice(0, maxRecentSearches)
    : [];

  // Get suggestion type icon
  const getSuggestionIcon = (type: SearchSuggestion['type']) => {
    switch (type) {
      case 'name':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        );
      case 'company':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21h18" />
            <path d="M5 21V7l8-4v18" />
            <path d="M19 21V11l-6-4" />
          </svg>
        );
      case 'category':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3" />
          </svg>
        );
      case 'keyword':
      case 'tag':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
        );
      case 'use_case':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11H5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h4" />
            <path d="M15 11h4a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-4" />
            <path d="M12 2v20" />
          </svg>
        );
      default:
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        );
    }
  };

  return (
    <div className={`solution-search ${className}`}>
      <div className="solution-search__input-container">
        <div className="solution-search__input-wrapper">
          <svg
            className="solution-search__search-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>

          <input
            ref={inputRef}
            type="text"
            className={`solution-search__input ${isFocused ? 'solution-search__input--focused' : ''}`}
            placeholder={placeholder}
            value={localSearchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />

          {loading && (
            <svg
              className="solution-search__loading-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
          )}

          {localSearchTerm && !loading && (
            <button
              className="solution-search__clear-button"
              onClick={handleClear}
              type="button"
              aria-label="Clear search"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Dropdown */}
        {showDropdown && (filteredSuggestions.length > 0 || filteredRecentSearches.length > 0) && (
          <div ref={dropdownRef} className="solution-search__dropdown">
            {/* Recent Searches */}
            {showRecentSearches && filteredRecentSearches.length > 0 && (
              <div className="solution-search__section">
                <div className="solution-search__section-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24" />
                  </svg>
                  Recent Searches
                </div>
                {filteredRecentSearches.map((term, index) => {
                  const globalIndex = (showSuggestions ? filteredSuggestions.length : 0) + index;
                  return (
                    <button
                      key={`recent-${index}`}
                      className={`solution-search__item ${selectedIndex === globalIndex ? 'solution-search__item--selected' : ''}`}
                      onClick={() => handleRecentSearchClick(term)}
                      type="button"
                    >
                      <svg
                        className="solution-search__item-icon"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="3" />
                        <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24" />
                      </svg>
                      <span className="solution-search__item-text">{term}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Suggestions */}
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div className="solution-search__section">
                <div className="solution-search__section-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                  Suggestions
                </div>
                {filteredSuggestions.map((suggestion, index) => (
                  <button
                    key={`suggestion-${index}`}
                    className={`solution-search__item ${selectedIndex === index ? 'solution-search__item--selected' : ''}`}
                    onClick={() => handleSuggestionClick(suggestion)}
                    type="button"
                  >
                    <span className="solution-search__item-icon">
                      {getSuggestionIcon(suggestion.type)}
                    </span>
                    <span className="solution-search__item-text">{suggestion.text}</span>
                    {suggestion.count !== undefined && (
                      <span className="solution-search__item-count">
                        ({suggestion.count})
                      </span>
                    )}
                    <span className="solution-search__item-type">
                      {suggestion.type}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SolutionSearch;
